import { environment } from '@env/environment';
import { AUTH_TOKEN_STORAGE_KEY } from '@core/services/api/auth.service';
import { isAtLeastLogLevel } from './client-log-level';
import type { ClientLogEvent, ClientLogSource } from './client-log-event.model';
import { getTopStackFrame, serializeClientError } from './error-serializer';
import { sanitizeMetadata, sanitizeString, sanitizeUrl } from './safe-log-sanitizer';

type ConsoleMethod = 'debug' | 'info' | 'log' | 'warn' | 'error';

const FALLBACK_SESSION_STORAGE_KEY = 'electa.clientLogs.fallbackSession';
const FALLBACK_DEDUPE_WINDOW_MS = 30_000;
const MAX_FALLBACK_REPORTS_PER_SESSION = 20;
const MAX_FALLBACK_REPORTS_PER_MINUTE = 5;
const nativeConsole = createNativeConsole();
let installed = false;
let fallbackReportsThisSession = 0;
const fallbackReportTimestamps: number[] = [];
const recentFingerprints = new Map<string, number>();

export const installProductionConsoleGuard = (): void => {
  try {
    if (installed || typeof console === 'undefined') return;
    if (environment.logging.consoleEnabled || environment.LOG) return;

    installed = true;

    const suppress = () => undefined;
    console.debug = suppress;
    console.info = suppress;
    console.log = suppress;
    console.warn = (...args: unknown[]) => {
      reportConsoleFallback('warn', args);
    };
    console.error = (...args: unknown[]) => {
      reportConsoleFallback('error', args);
    };
  } catch {
    installed = true;
  }
};

export const reportBootstrapError = (error: unknown): void => {
  try {
    if (environment.logging.consoleEnabled || environment.LOG) {
      nativeConsole.error('Failed to bootstrap application', error);
      return;
    }

    sendFallbackEvent('fatal', 'bootstrap', 'Failed to bootstrap application', error);
  } catch {
    // Bootstrap logging must never throw while Angular is unavailable.
  }
};

const reportConsoleFallback = (level: 'warn' | 'error', args: unknown[]): void => {
  try {
    if (!environment.logging.captureConsoleFallback) return;
    if (!isAtLeastLogLevel(level, environment.logging.remoteMinLevel)) return;

    const [first, ...rest] = args;
    const firstSerialized = serializeClientError(first, `Console ${level}`);
    const message = typeof first === 'string'
      ? first
      : firstSerialized.message;

    sendFallbackEvent(level, 'console-fallback', message, first, {
      arguments: rest.map((item) => serializeClientError(item, 'Console argument').message),
    });
  } catch {
    // console fallback must never recurse or throw
  }
};

const sendFallbackEvent = (
  level: 'warn' | 'error' | 'fatal',
  source: ClientLogSource,
  message: string,
  error?: unknown,
  metadata?: Record<string, unknown>,
): void => {
  try {
    if (!environment.logging.remoteEnabled) return;
    if (!isAtLeastLogLevel(level, environment.logging.remoteMinLevel)) return;
    if (!canSendFallbackReport()) return;

    const token = readAuthToken();
    if (!token) return;

    const serialized = serializeClientError(error, message);
    const sanitizedMessage = sanitizeString(message || serialized.message, 1000);
    const stack = serialized.stack ? sanitizeString(serialized.stack, 10000) : undefined;
    const fingerprint = createFingerprint(
      level,
      source,
      sanitizedMessage,
      serialized.errorName,
      getTopStackFrame(stack),
    );

    if (isDuplicate(fingerprint)) return;

    const event: ClientLogEvent = {
      severity: level,
      source,
      message: sanitizedMessage,
      errorName: serialized.errorName,
      stack,
      fingerprint,
      pageUrl: sanitizeUrl(typeof window !== 'undefined' ? window.location.href : undefined),
      appVersion: environment.appVersion,
      environmentState: environment.state,
      sessionId: getOrCreateFallbackSessionId(),
      metadata: sanitizeMetadata(metadata),
      occurredAt: new Date().toISOString(),
    };

    fallbackReportsThisSession++;
    fallbackReportTimestamps.push(Date.now());

    fetch(`${environment.apiUrl}/client-error-logs/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events: [event] }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // fallback delivery is best-effort only
  }
};

const canSendFallbackReport = (): boolean => {
  if (fallbackReportsThisSession >= MAX_FALLBACK_REPORTS_PER_SESSION) return false;

  const now = Date.now();
  const recent = fallbackReportTimestamps.filter((timestamp) => now - timestamp < 60_000);
  fallbackReportTimestamps.length = 0;
  fallbackReportTimestamps.push(...recent);

  return recent.length < MAX_FALLBACK_REPORTS_PER_MINUTE;
};

const isDuplicate = (fingerprint: string): boolean => {
  const now = Date.now();
  const previous = recentFingerprints.get(fingerprint);
  recentFingerprints.set(fingerprint, now);
  return previous !== undefined && now - previous < FALLBACK_DEDUPE_WINDOW_MS;
};

const readAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const getOrCreateFallbackSessionId = (): string => {
  if (typeof window === 'undefined') return createUuid();

  try {
    const existing = sessionStorage.getItem(FALLBACK_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const next = createUuid();
    sessionStorage.setItem(FALLBACK_SESSION_STORAGE_KEY, next);
    return next;
  } catch {
    return createUuid();
  }
};

const createFingerprint = (...parts: (string | undefined)[]): string => {
  const input = parts.filter(Boolean).join('|').toLowerCase();
  let hash = 0;

  for (let index = 0; index < input.length; index++) {
    hash = (hash * 31 + input.charCodeAt(index)) >>> 0;
  }

  return hash.toString(36);
};

const createUuid = (): string => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to Math.random-based fallback
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

function createNativeConsole(): Record<ConsoleMethod, (...args: unknown[]) => void> {
  if (typeof console === 'undefined') {
    const noop = () => undefined;
    return { debug: noop, info: noop, log: noop, warn: noop, error: noop };
  }

  const bindMethod = (method: ConsoleMethod): ((...args: unknown[]) => void) => {
    const candidate = console[method];
    return typeof candidate === 'function' ? candidate.bind(console) : () => undefined;
  };

  return {
    debug: bindMethod('debug'),
    info: bindMethod('info'),
    log: bindMethod('log'),
    warn: bindMethod('warn'),
    error: bindMethod('error'),
  };
}
